import { isTauriRuntime } from '@/helpers/resolvedPlayerAppVersion'
import { getPlatform } from '@/platform'

/** Mirrors WifiValidation.kt. SSID is 1–32 UTF-8 bytes. */
export function validateWifiSsid(ssid: string): 'empty' | 'tooLong' | null {
  const n = new TextEncoder().encode(ssid).length
  if (n < 1) {
    return 'empty'
  }
  if (n > 32) {
    return 'tooLong'
  }
  return null
}

export function isWifiHex64(psk: string): boolean {
  return psk.length === 64 && /^[0-9a-f]+$/i.test(psk)
}

/** 8–63 printable ASCII, or exactly 64 hex digits. */
export function validateWifiPsk(psk: string): 'tooShort' | 'tooLong' | 'notPrintable' | null {
  if (isWifiHex64(psk)) {
    return null
  }
  if (psk.length < 8) {
    return 'tooShort'
  }
  if (psk.length > 63) {
    return 'tooLong'
  }
  for (let i = 0; i < psk.length; i++) {
    const code = psk.charCodeAt(i)
    if (code < 32 || code > 126) {
      return 'notPrintable'
    }
  }
  return null
}

function escapeWifiQuoted(value: string): string {
  return value.replaceAll('\\', '\\\\').replaceAll('"', '\\"')
}

export function quoteWifiSsid(ssid: string): string {
  return `"${escapeWifiQuoted(ssid)}"`
}

export function quoteWifiPsk(psk: string): string {
  if (isWifiHex64(psk)) {
    return psk
  }
  return `"${escapeWifiQuoted(psk)}"`
}

/** Same gate as WifiValidation.shouldReapply: no addNetwork unless all four say yes. */
export function shouldReapplyWifi(input: {
  hasSavedCreds: boolean
  deviceOwner: boolean
  hasUsableConnection: boolean
  configuredNetworkMatchesSaved: boolean
}): boolean {
  if (!input.hasSavedCreds || !input.deviceOwner) {
    return false
  }
  if (input.hasUsableConnection || input.configuredNetworkMatchesSaved) {
    return false
  }
  return true
}

/** SSID byte length only. Never pass a PSK. */
export function redactWifiSsid(ssid: string): string {
  return `ssidBytes=${new TextEncoder().encode(ssid).length}`
}

export function wifiConfigureLogLine(ssid: string): string {
  return `wifiConfigure ${redactWifiSsid(ssid)}`
}

export const WIFI_FAIL_REASONS = ['auth', 'notFound', 'timeout', 'noInternet', 'dpcRefused', 'notOwner', 'invalid'] as const

export type TWifiFailReason = typeof WIFI_FAIL_REASONS[number]

export type TWifiRestorePhase = 'idle' | 'restoring' | 'retrying'

export interface TWifiSavedPublic {
  ssid: string
  savedAtMs: number
  lastRestoreAtMs: number
}

export interface TWifiStatus {
  deviceOwner: boolean
  connectionType: string
  currentSsid: string | null
  signalLevel: number | null
  ipv4: string | null
  saved: TWifiSavedPublic | null
  restore: TWifiRestorePhase
  nextAttemptMs: number
}

export interface TWifiConfigureResult {
  ok: boolean
  reason: TWifiFailReason | null
}

export function parseWifiStatus(raw: unknown): TWifiStatus | null {
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const o = raw as Record<string, unknown>
  if (typeof o.deviceOwner !== 'boolean') {
    return null
  }
  const restore = o.restore === 'restoring' || o.restore === 'retrying' || o.restore === 'idle'
    ? o.restore
    : 'idle'
  return {
    deviceOwner: o.deviceOwner,
    connectionType: typeof o.connectionType === 'string' && o.connectionType.trim() ? o.connectionType.trim() : 'unknown',
    currentSsid: typeof o.currentSsid === 'string' && o.currentSsid.trim() ? o.currentSsid : null,
    signalLevel: typeof o.signalLevel === 'number' ? o.signalLevel : null,
    ipv4: typeof o.ipv4 === 'string' && o.ipv4.trim() ? o.ipv4 : null,
    saved: parseSaved(o.saved),
    restore,
    nextAttemptMs: typeof o.nextAttemptMs === 'number' ? o.nextAttemptMs : 0
  }
}

function parseSaved(raw: unknown): TWifiSavedPublic | null {
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const o = raw as Record<string, unknown>
  if (typeof o.ssid !== 'string' || o.ssid.length < 1) {
    return null
  }
  return {
    ssid: o.ssid,
    savedAtMs: typeof o.savedAtMs === 'number' ? o.savedAtMs : 0,
    lastRestoreAtMs: typeof o.lastRestoreAtMs === 'number' ? o.lastRestoreAtMs : 0
  }
}

export function parseWifiConfigureResult(raw: unknown): TWifiConfigureResult | null {
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const o = raw as Record<string, unknown>
  if (typeof o.ok !== 'boolean') {
    return null
  }
  const reason = typeof o.reason === 'string' && (WIFI_FAIL_REASONS as readonly string[]).includes(o.reason)
    ? o.reason as TWifiFailReason
    : null
  return { ok: o.ok, reason: o.ok ? null : reason }
}

async function invokePlugin<T>(command: string, args?: Record<string, unknown>): Promise<T | null> {
  if (!isTauriRuntime() || getPlatform() !== 'android') {
    return null
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return await invoke<T>(`plugin:lucimark-android|${command}`, args)
  }
  catch {
    return null
  }
}

export async function fetchWifiStatus(): Promise<TWifiStatus | null> {
  const raw = await invokePlugin<unknown>('wifiGetStatus')
  if (raw == null) {
    return null
  }
  return parseWifiStatus(raw)
}

export async function configureWifi(input: { ssid: string, psk: string }): Promise<TWifiConfigureResult | null> {
  const raw = await invokePlugin<unknown>('wifiConfigure', { ssid: input.ssid, psk: input.psk })
  if (raw == null) {
    return null
  }
  return parseWifiConfigureResult(raw)
}

export async function clearSavedWifi(): Promise<boolean> {
  const raw = await invokePlugin<unknown>('wifiClearSaved')
  if (raw == null || typeof raw !== 'object') {
    return false
  }
  return (raw as { ok?: unknown }).ok === true
}
